import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaTrashAlt, FaShoppingCart, FaArrowRight, FaCheckCircle, FaTag, FaCcVisa, FaCcMastercard, FaCcAmex } from "react-icons/fa";
import { SiUpi } from "react-icons/si";
import { useApp } from "../App";
import api from "../services/api";
import { BASE_URL } from "../config";
import "../styles/CartPage.css";

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQty, cartTotal, showToast } = useApp();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountAmt, setDiscountAmt] = useState(0);
  const [validating, setValidating] = useState(false);

  const FREE_SHIPPING_THRESHOLD = 1000;
  const shipping = cartTotal >= FREE_SHIPPING_THRESHOLD ? 0 : 80;
  const total = cartTotal - discountAmt + shipping;
  const progressPct = Math.min(100, (cartTotal / FREE_SHIPPING_THRESHOLD) * 100);
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - cartTotal);

  const applyCoupon = async () => {
    const code = coupon.trim().toUpperCase();
    if (!code) return;
    setValidating(true);
    try {
      const data = await api.coupons.validate(code, cartTotal);
      setDiscount(data.discountValue);
      setDiscountAmt(data.discountAmount);
      showToast(`Coupon "${code}" applied! You save ₹${data.discountAmount}.`);
    } catch (err) {
      setDiscount(0); setDiscountAmt(0);
      showToast(err.message || "Invalid coupon code.", "error");
    } finally { setValidating(false); }
  };

  if (cart.length === 0) return (
    <div className="page-wrapper empty-cart-wrapper">
      <div className="container">
        <div className="cart-empty-card fade-in">
          <div className="cart-empty-icon"><FaShoppingCart /></div>
          <h2>Your cart is empty</h2>
          <p>Looks like you haven't added any products yet. Discover our latest collections!</p>
          <Link className="btn btn-primary btn-lg" to="/collection">
            Continue Shopping <FaArrowRight style={{ marginLeft: 8 }} />
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="container">

        {/* Breadcrumb */}
        <nav className="page-breadcrumb">
          <Link to="/">Home</Link>
          <span className="separator">/</span>
          <span className="current">Your Cart</span>
        </nav>

        <div className="cart-layout">

          {/* Cart Items Section */}
          <div className="cart-items-section">
            <div className="cart-header">
              <h1 className="page-title">Shopping Cart <span className="item-count">({cart.length} items)</span></h1>
            </div>

            <div className="cart-items-list">
              {cart.map(item => (
                <div className="cart-item-card" key={item.key}>
                  <div className="cart-item-img" onClick={() => navigate(`/product/${item.productId}`)}>
                    <img src={`${BASE_URL}${item.image}`} alt={item.name} onError={e => { e.target.src = `https://placehold.co/90x110/f0e8dc/8B1A1A?text=IMG`; }} />
                  </div>

                  <div className="cart-item-info">
                    <div className="cart-item-header">
                      <div>
                        <div className="cart-item-brand">{item.brand}</div>
                        <div className="cart-item-name" onClick={() => navigate(`/product/${item.productId}`)}>{item.name}</div>
                        <div className="cart-item-meta">Size: <span>{item.size}</span></div>
                      </div>
                      <div className="cart-item-price-mobile">₹{(item.price * item.qty).toLocaleString()}</div>
                    </div>

                    <div className="cart-item-controls">
                      <div className="qty-selector">
                        <button className="qty-btn" onClick={() => updateQty(item.key, item.qty - 1)}>−</button>
                        <input className="qty-input" type="number" value={item.qty} min={1} onChange={e => updateQty(item.key, Math.max(1, Number(e.target.value)))} />
                        <button className="qty-btn" onClick={() => updateQty(item.key, item.qty + 1)}>+</button>
                      </div>

                      <div className="cart-item-actions">
                        <span className="cart-item-price desktop-only">₹{(item.price * item.qty).toLocaleString()}</span>
                        <button className="btn-icon-remove" onClick={() => removeFromCart(item.key)} aria-label="Remove item">
                          <FaTrashAlt />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary Sticky Card */}
          <div className="cart-summary-wrapper">
            <div className="cart-summary-card">
              <h3>Order Summary</h3>

              {/* Gamified Shipping Bar */}
              <div className="shipping-progress-box">
                {remaining > 0 ? (
                  <p className="shipping-text">Add <strong>₹{remaining}</strong> more for FREE shipping! 🚚</p>
                ) : (
                  <p className="shipping-success"><FaCheckCircle /> You've unlocked FREE shipping!</p>
                )}
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progressPct}%`, backgroundColor: remaining > 0 ? 'var(--accent)' : 'var(--success)' }} />
                </div>
              </div>

              {/* Coupon Section */}
              <div className="coupon-section">
                <div className="coupon-input-group">
                  <FaTag className="coupon-icon" />
                  <input className="coupon-input" type="text" placeholder="Coupon code" value={coupon}
                    onChange={e => setCoupon(e.target.value)} onKeyDown={e => e.key === "Enter" && applyCoupon()} />
                  <button className="btn btn-primary btn-sm coupon-btn" onClick={applyCoupon} disabled={validating}>
                    {validating ? "…" : "Apply"}
                  </button>
                </div>
                {/* <p className="coupon-hint">Try: NANDU10 · FESTIVE60</p> */}
              </div>

              <div className="summary-divider" />

              {/* Totals */}
              <div className="summary-row">
                <span className="summary-label">Subtotal</span>
                <span className="summary-value">₹{cartTotal.toLocaleString()}</span>
              </div>

              {discountAmt > 0 && (
                <div className="summary-row discount-row">
                  <span className="summary-label">Discount ({discount}%)</span>
                  <span className="summary-value">−₹{discountAmt.toLocaleString()}</span>
                </div>
              )}

              <div className="summary-row">
                <span className="summary-label">Shipping</span>
                <span className="summary-value">{shipping === 0 ? <span className="free-badge">FREE</span> : `₹${shipping}`}</span>
              </div>

              <div className="summary-divider" />

              <div className="summary-row total-row">
                <span>Total</span>
                <span>₹{total.toLocaleString()}</span>
              </div>

              <button className="btn btn-primary btn-full checkout-btn"
                onClick={() => navigate("/checkout", { state: { discountAmt, couponCode: coupon.trim().toUpperCase(), discount } })}>
                Proceed to Checkout <FaArrowRight />
              </button>

              <p className="secure-checkout-note">🔒 Secure & Encrypted Checkout</p>

              <div className="payment-methods" style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "28px", color: "var(--border)" }}>
                <FaCcVisa title="Visa" style={{ transition: "0.3s", cursor: "default" }} onMouseOver={e => e.target.style.color = "var(--secondary)"} onMouseOut={e => e.target.style.color = "var(--border)"} />
                <FaCcMastercard title="Mastercard" style={{ transition: "0.3s", cursor: "default" }} onMouseOver={e => e.target.style.color = "var(--secondary)"} onMouseOut={e => e.target.style.color = "var(--border)"} />

                {/* Replaced SiUpi with a clean, heavily stylized text badge */}
                <span
                  title="UPI"
                  style={{
                    fontSize: "14px",
                    fontWeight: "800",
                    border: "2px solid currentColor",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    letterSpacing: "1px",
                    display: "flex",
                    alignItems: "center",
                    height: "24px",
                    transition: "0.3s",
                    cursor: "default"
                  }}
                  onMouseOver={e => e.target.style.color = "var(--secondary)"}
                  onMouseOut={e => e.target.style.color = "var(--border)"}
                >
                  UPI
                </span>

                <FaCcAmex title="American Express" style={{ transition: "0.3s", cursor: "default" }} onMouseOver={e => e.target.style.color = "var(--secondary)"} onMouseOut={e => e.target.style.color = "var(--border)"} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}